import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import HistoryIcon from '@mui/icons-material/History';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

interface HelpDialogProps {
  open: boolean;
  onClose: () => void;
  includeSflaGaBaseline?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

interface SectionCardProps {
  title: string;
  children: React.ReactNode;
}

const SectionCard: React.FC<SectionCardProps> = ({ title, children }) => (
  <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <List dense sx={{ py: 0 }}>
    {items.map((item) => (
      <ListItem key={item} sx={{ px: 0, alignItems: 'flex-start' }}>
        <ListItemIcon sx={{ minWidth: 28, mt: 0.25 }}>
          <CheckCircleIcon color="primary" fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={item}
          primaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    ))}
  </List>
);

const HelpDialog: React.FC<HelpDialogProps> = ({ open, onClose, includeSflaGaBaseline = false }) => {
  const theme = useTheme();
  const [value, setValue] = useState(0);
  const pipelineDescription = includeSflaGaBaseline
    ? 'The active optimization pipeline runs 7 algorithms: GA, Cultural, SFLA, the standard SFLA-GA baseline, the proposed SFLAGA variant, PSO, and GAPSO.'
    : 'The active optimization pipeline runs 6 algorithms: GA, Cultural, SFLA, the proposed SFLAGA variant, PSO, and GAPSO.';

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const tabs = [
    { label: 'Getting Started', icon: <CheckCircleIcon /> },
    { label: 'Configuration', icon: <SettingsIcon /> },
    { label: 'Upload Dataset', icon: <CloudUploadIcon /> },
    { label: 'Run Optimization', icon: <PlayArrowIcon /> },
    { label: 'View Results', icon: <TableChartIcon /> },
    { label: 'Charts', icon: <BarChartIcon /> },
    { label: 'Export Data', icon: <FileDownloadIcon /> },
    { label: 'History', icon: <HistoryIcon /> },
    { label: 'Complete Run', icon: <AutoAwesomeIcon /> },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            User Guide
          </Typography>
        </Box>
        <Button
          onClick={onClose}
          sx={{
            color: 'white',
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: alpha('#ffffff', 0.1),
            },
          }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', height: 'calc(90vh - 120px)' }}>
          <Box
            sx={{
              borderRight: 1,
              borderColor: 'divider',
              width: 220,
              backgroundColor: alpha(theme.palette.primary.main, 0.02),
            }}
          >
            <Tabs
              orientation="vertical"
              value={value}
              onChange={handleChange}
              sx={{
                borderRight: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textAlign: 'left',
                  alignItems: 'flex-start',
                  minHeight: 48,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                },
              }}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.label}
                  label={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            <TabPanel value={value} index={0}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                Getting Started
              </Typography>

              <SectionCard title="Accessing the System">
                <BulletList
                  items={[
                    'Open the application URL in your browser.',
                    'Use "Access the System" on the landing page to reach login.',
                    'Sign in to open the main optimization workspace.',
                    'Use "Download Dataset" in the header if you want the bundled sample file.',
                  ]}
                />
              </SectionCard>

              <SectionCard title="Main Workspace">
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.05), height: '100%' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Header
                      </Typography>
                      <Typography variant="body2">
                        Dataset download, Search UI, Help, optional Algorithms modal, G and J benchmark buttons, exports, Start Optimization, Complete Run, Cancel Optimization, and Logout.
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.secondary.main, 0.05), height: '100%' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Left Sidebar
                      </Typography>
                      <Typography variant="body2">
                        Parameters and Dataset upload panels, including Parallel and the optional SFLA-GA baseline toggle. The sidebar can be hidden and shown.
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.success.main, 0.05), height: '100%' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Center and Right Panels
                      </Typography>
                      <Typography variant="body2">
                        Convergence Live Simulation, Energy Live Simulation, Progress, Results, Charts, History, plus the Energy Leaderboard and Combined Winner summary cards.
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </SectionCard>

              <SectionCard title="Quick Navigation and Live Panels">
                <BulletList
                  items={[
                    'Use "Search UI" in the header, or press Ctrl+K / Cmd+K, to jump directly to Results, Charts, Statistics, or History.',
                    'Convergence Live Simulation streams fitness curves during a run and lets you click an iteration for an explanation snapshot.',
                    'The live convergence chart can be maximized or restored from its panel controls.',
                    'Energy Live Simulation updates as each algorithm completes.',
                    'The right sidebar can be hidden on desktop and reopened with the chevron toggle or the Show Right Panel button on smaller screens.',
                  ]}
                />
              </SectionCard>

              <SectionCard title="Algorithms in the Current Build">
                <BulletList
                  items={[
                    pipelineDescription,
                    'Some tables and charts use "Culture" for Cultural Algorithm and "Frog" for SFLA.',
                    'If the optional Algorithms button is enabled, its modal also includes reference content for Kurd SFLA and Scientific Comparison.',
                  ]}
                />
              </SectionCard>
            </TabPanel>

            <TabPanel value={value} index={1}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                Configuring Parameters
              </Typography>

              <SectionCard title="Common Parameters">
                <BulletList
                  items={[
                    'Workflow Tasks controls the composition size and defaults to 10.',
                    'QoS Properties is fixed at 9 in the current UI and is informational only.',
                    'Changing Workflow Tasks reapplies the shared benchmark configuration used for fair algorithm comparison.',
                    'The shared benchmark formulas are: Iterations = floor(taskCount / 10) + 40, Initial Population = taskCount + 40, and Memeplex Count = round(initialPopulation / 20) with safety bounds.',
                  ]}
                />
              </SectionCard>

              <SectionCard title="Execution Controls">
                <BulletList
                  items={[
                    'Parallel runs algorithms in parallel using Web Workers when the browser supports it.',
                    'SFLA-GA is an optional baseline comparator that adds the standard SFLA-GA hybrid to the pipeline.',
                    'You can still override visible parameter values manually before starting a run.',
                  ]}
                />
              </SectionCard>

              <SectionCard title="Algorithm Tuning">
                <BulletList
                  items={[
                    'Selection Rate defaults to 60% and is shared by the GA-based selection phases that use it.',
                    'Crossover Rate and Mutation Rate auto-adjust by workflow size under the shared benchmark policy instead of an algorithm-specific tuning rule.',
                    'Memeplex Count is shared across the SFLA-family algorithms.',
                    'Mutation Rate is rounded to a whole number in the panel.',
                    'You can still override any visible value manually before running the optimizer.',
                  ]}
                />
              </SectionCard>

              <SectionCard title="Fitness Mode">
                <BulletList
                  items={[
                    'QoS Only uses QoS quality only.',
                    'Energy-Aware uses a weighted combination of QoS and Energy while still reporting Cost separately.',
                    'Default Energy-Aware weights are 0.625 for QoS and 0.375 for Energy.',
                    'Changing one Energy-Aware weight automatically sets the other so the pair always sums to 1.0.',
                  ]}
                />
              </SectionCard>

              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Number of Runs</strong> defaults to 5 in the panel. Any value above 1 enables the statistical workflow and the <strong>Stats</strong> tab. If you change <strong>Workflow Tasks</strong>, the panel reapplies its auto-derived values and resets <strong>Number of Runs</strong> back to 5.
                </Typography>
              </Alert>
            </TabPanel>

            <TabPanel value={value} index={2}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                Uploading Datasets
              </Typography>

              <SectionCard title="Accepted Input">
                <BulletList
                  items={[
                    'The Dataset panel accepts .csv and .txt files.',
                    'Use the same comma-separated layout as the bundled sample dataset.',
                    'The current parser expects headerless data rows rather than a labeled header row.',
                  ]}
                />
              </SectionCard>

              <SectionCard title="Expected Column Order">
                <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-line' }}>
                  ResponseTime
                  {'\n'}Availability
                  {'\n'}Throughput
                  {'\n'}Successability
                  {'\n'}Reliability
                  {'\n'}Compliance
                  {'\n'}BestPractices
                  {'\n'}Latency
                  {'\n'}Documentation
                  {'\n'}ServiceName (optional)
                  {'\n'}WSDL_URL (optional)
                </Typography>
              </SectionCard>

              <SectionCard title="Upload Steps">
                <BulletList
                  items={[
                    'Open the Dataset panel in the left sidebar.',
                    'Click "Browse Dataset".',
                    'Choose your .csv or .txt file.',
                    'Confirm the selected filename and size appear in the panel.',
                    'Start the optimization. The dataset is parsed when the run begins.',
                  ]}
                />
              </SectionCard>

              <Alert severity="warning">
                <Typography variant="body2">
                  Common problems are wrong column order, non-numeric QoS values, a header row, or using a delimiter other than a comma.
                </Typography>
              </Alert>
            </TabPanel>

            <TabPanel value={value} index={3}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                Running Optimizations
              </Typography>

              <SectionCard title="Before You Start">
                <BulletList
                  items={[
                    'Configure the parameters you want.',
                    'Select a dataset file.',
                    'Choose single-run or multi-run statistics.',
                    'Review the fitness mode before you click Start Optimization.',
                  ]}
                />
              </SectionCard>

              <SectionCard title="What Happens During a Run">
                <BulletList
                  items={[
                    'Click "Start Optimization" in the header.',
                    'The app loads the dataset for the current task count.',
                    pipelineDescription,
                    'If the SFLA-GA checkbox is enabled, the baseline is included in the current run.',
                    'Results, live simulation panels, and summary cards update as algorithms finish.',
                    'Completed runs are stored in local history.',
                  ]}
                />
              </SectionCard>

              <SectionCard title="Progress Panel">
                <BulletList
                  items={[
                    'Overall progress percentage',
                    'Current algorithm',
                    'Current iteration',
                    'Run progress for multi-run analysis',
                    'Per-algorithm completion chips and metric snapshots',
                    'Live convergence and execution-time updates while the run is active',
                  ]}
                />
              </SectionCard>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  If <strong>Number of Runs</strong> is greater than 1, the optimizer repeats the experiment for the requested run count and generates the <strong>Stats</strong> tab.
                </Typography>
              </Alert>

              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>Cancel Optimization</strong> stops an active run cooperatively, so the current algorithm stops at its next cancellation point rather than instantly.
                </Typography>
              </Alert>
            </TabPanel>

            <TabPanel value={value} index={4}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                Viewing Results
              </Typography>

              <SectionCard title="Current Result Tabs">
                <BulletList
                  items={[
                    'Solutions',
                    'Fitness',
                    'Config',
                    'Energy',
                    'Cost',
                    'RT',
                    'Analysis',
                    'Combined Winner',
                    'Stats (only when statistical results exist)',
                  ]}
                />
              </SectionCard>

              <SectionCard title="What the Tabs Mean">
                <BulletList
                  items={[
                    'Solutions shows selected service indices for each algorithm.',
                    'Fitness, Energy, Cost, and RT provide direct comparisons of the core metrics.',
                    'Analysis gives text-based rankings and winners.',
                    'Combined Winner applies an energy-first ranking: lowest energy first, then composite score and fitness.',
                    'Stats adds median, mean, std dev, min, max, the derived QTI table, composite scores, Pareto analysis, and Export Statistics when available.',
                  ]}
                />
              </SectionCard>

              <Alert severity="success">
                <Typography variant="body2">
                  Result tables highlight best values in green and worst values in red to make the current leaders easy to scan.
                </Typography>
              </Alert>
            </TabPanel>

            <TabPanel value={value} index={5}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                Understanding Charts
              </Typography>

              <SectionCard title="Chart Tabs in the Current Build">
                <Grid container spacing={1}>
                  {[
                    'Fitness Comparison',
                    'Convergence',
                    'Similarity',
                    'Energy',
                    'Cost',
                    'Response Time',
                    'Execution Time',
                    'QTI (Derived)',
                    'Normalized',
                    'Multi-Metric',
                    'Convergence Speed',
                    'Combined Winner',
                    'Energy Box Plot',
                  ].map((label) => (
                    <Grid item xs={12} sm={6} key={label}>
                      <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <Typography variant="body2">{label}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </SectionCard>

              <SectionCard title="How to Read Them">
                <BulletList
                  items={[
                    'QTI (Derived) is a reporting metric based on Fitness / (1 + normalized execution time); it is not part of the optimizer objective.',
                    'Similarity compares every algorithm against SFLAGA.',
                    'Normalized converts metrics to a 0 to 1 scale for easier cross-metric comparison.',
                    'Multi-Metric can include QTI and Composite Score when those values are available.',
                    'Convergence Speed compares improvement rate and roughly when each algorithm reaches 90% of its final improvement.',
                    'Energy Box Plot is mainly useful after multi-run analysis.',
                    'Many chart panels can be maximized for easier inspection.',
                  ]}
                />
              </SectionCard>
            </TabPanel>

            <TabPanel value={value} index={6}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                Exporting Data
              </Typography>

              <SectionCard title="Available Exports">
                <BulletList
                  items={[
                    'Download Dataset from the header for the bundled sample file.',
                    'Export Excel for the main optimization workbook.',
                    'Export Analysis for the text ranking report.',
                    'Export Statistics from the Stats tab when statistical results exist.',
                    'Export Comparison from the History comparison dialog.',
                    'Complete Run exports automatically when the full range finishes.',
                  ]}
                />
              </SectionCard>

              <SectionCard title="Typical File Names">
                <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-line' }}>
                  optimization_results_[timestamp].xlsx
                  {'\n'}analysis_report_[timestamp].txt
                  {'\n'}statistics_results_[timestamp].xlsx
                  {'\n'}comparison_results_[timestamp].xlsx
                  {'\n'}complete_run_[start]_to_[end]_[timestamp].xlsx
                </Typography>
              </SectionCard>

              <Alert severity="info">
                <Typography variant="body2">
                  Export the runs you care about before deleting history. Regular export buttons in the header only work when a result is currently loaded.
                </Typography>
              </Alert>
            </TabPanel>

            <TabPanel value={value} index={7}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                History Management
              </Typography>

              <SectionCard title="History Table">
                <BulletList
                  items={[
                    'Each row shows timestamp, task count, initial population, and run count.',
                    'Click a row to open the details dialog.',
                    'The details dialog includes results, a comprehensive comparison chart, and statistics when available.',
                  ]}
                />
              </SectionCard>

              <SectionCard title="History Actions">
                <BulletList
                  items={[
                    'Compare lets you select up to 3 saved entries.',
                    'Refresh reloads history from local storage.',
                    'The trash icon deletes one row.',
                    'Clear All removes the full history after confirmation.',
                  ]}
                />
              </SectionCard>

              <Alert severity="warning">
                <Typography variant="body2">
                  Clearing all history cannot be undone. Export important results first if you need an external record.
                </Typography>
              </Alert>
            </TabPanel>

            <TabPanel value={value} index={8}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                Complete Run Feature
              </Typography>

              <SectionCard title="What Complete Run Does">
                <BulletList
                  items={[
                    'Runs a task-count range sequentially instead of a single task size.',
                    'Useful for scalability studies, benchmark sweeps, and export-ready summary workbooks.',
                    'Updates the visible parameter panel and results after each task count completes.',
                  ]}
                />
              </SectionCard>

              <SectionCard title="Configuration Fields">
                <BulletList
                  items={[
                    'Workflow Tasks Start',
                    'Workflow Tasks End',
                    'Increment Range',
                    'Summary panel showing the total number of runs in the range',
                  ]}
                />
              </SectionCard>

              <SectionCard title="Validation and Behavior">
                <BulletList
                  items={[
                    'Start must be at least 1.',
                    'End must be greater than or equal to Start.',
                    'Increment must be at least 1.',
                    'Increment must not exceed End - Start, so the current dialog expects at least two task counts in the range.',
                    'If Number of Runs is greater than 1, each task count uses multi-run statistics before being added to the final workbook.',
                  ]}
                />
              </SectionCard>

              <Alert severity="success">
                <Typography variant="body2">
                  At the end of Complete Run, the app automatically exports a workbook with metrics, rankings, detailed results, solutions, configuration, and combined-winner sheets across the full task range.
                </Typography>
              </Alert>
            </TabPanel>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HelpDialog;
